import { useEffect, useRef } from 'react';
import { isEmpty, orderBy } from 'lodash';
import * as MixpanelAudience from 'react/infra/mixpanel/MixpanelAudience';
import { ALL_INTERESTS } from '../constants';

const useInterestTesting = ({
  segment,
  interestType,
  interestTypes,
  interests,
  audienceChannel,
  context,
  setInterests,
  setTopic,
  newlyAddedInterestsToTest,
  setNewlyAddedInterestsToTest,
  saveTestedInterestsToContext,
  processNewTestedInterests,
  getPromiseOfInterestsByTypeAndTopic,
  doPublishAllInterestsToView,
}) => {
  // Track restoration state per type to prevent duplicate API calls
  const restorationStateRef = useRef({});

  // Track previous segment to detect changes
  const prevSegmentRef = useRef(segment);

  // Clear restoration state when segment changes
  useEffect(() => {
    if (prevSegmentRef.current === segment) return;
    prevSegmentRef.current = segment;

    // Reset restoration state so restoration can run fresh for new segment
    restorationStateRef.current = {};
  }, [segment]);

  // Ref to track current interest type - prevents stale callbacks from updating wrong data
  const currentInterestTypeRef = useRef(interestType);
  useEffect(() => {
    currentInterestTypeRef.current = interestType;
  }, [interestType]);

  // Restore highlighted categories
  useEffect(() => {
    if (interestType !== 'categories' || !interests?.length) return;

    const savedCategories = context.current.audience_app[audienceChannel].audience_interestsToTest.categories || [];
    if (isEmpty(savedCategories)) return;

    const savedIds = savedCategories.map((c) => c?.id).filter(Boolean);

    // Check if any saved categories need to be added (not in current list) or highlighted
    const existingIds = new Set(interests.map((item) => item.id));
    const needsAdd = savedCategories.some((c) => c?.id && !existingIds.has(c.id));
    const needsHighlight = interests.some((item) => savedIds.includes(item.id) && !item.highlighted);

    if (needsAdd || needsHighlight) {
      setInterests((prev) => {
        const existingIdsSet = new Set(prev?.map((item) => item.id) || []);

        // Add categories that aren't in the current list
        const newCategories = savedCategories
          .filter((c) => c?.id && !existingIdsSet.has(c.id))
          .map((c) => ({
            ...c,
            highlighted: true,
            deletable: true,
            highlightMarkTooltip: 'Manually added',
            wasAddedFromSearch: true, // Mark as added from search for proper deletion
          }));

        // Highlight existing items that match
        const mapped = prev?.map((item) =>
          savedIds.includes(item.id)
            ? { ...item, highlighted: true, deletable: true, highlightMarkTooltip: 'Manually added' }
            : item
        );

        // Combine: new categories + mapped existing
        const combined = [...newCategories, ...(mapped || [])];

        // Sort: highlighted at top (desc), then by uniqueness-index desc
        return orderBy(combined, ['highlighted', 'uniqueness-index'], ['desc', 'desc']);
      });
    }
  }, [interestType, interests, audienceChannel, context, setInterests]);

  // Quick highlight: Immediately mark any saved interests that exist in current top interests
  // This provides instant visual feedback while restoration API runs in background
  useEffect(() => {
    if (interestType === 'categories' || !interests?.length) return;

    const savedInterests = context.current.audience_app[audienceChannel].audience_interestsToTest?.[interestType] || [];
    if (isEmpty(savedInterests)) return;

    const savedIds = savedInterests.map((i) => i.id);
    const hasUnhighlightedSaved = interests.some((item) => savedIds.includes(item.id) && !item.highlighted);

    if (hasUnhighlightedSaved) {
      setInterests((prev) => {
        const mapped = prev?.map((item) =>
          savedIds.includes(item.id)
            ? { ...item, highlighted: true, deletable: true, highlightMarkTooltip: 'Manually added' }
            : item
        );
        // Sort: highlighted at top (desc), then by uniqueness-index desc
        return orderBy(mapped, ['highlighted', 'uniqueness-index'], ['desc', 'desc']);
      });
    }
  }, [interestType, interests, audienceChannel, context, setInterests]);

  // Restore highlighted interests for non-category types (phrases, websites, searches)
  // This fetches fresh data for saved interests that might not be in top interests
  useEffect(() => {
    // Skip if categories or no interests loaded yet
    if (interestType === 'categories' || !interests?.length) return;

    // Skip if there's a pending add operation - let the add flow handle it
    const hasPendingAdd = interestTypes.some(({ value: type }) => !isEmpty(newlyAddedInterestsToTest[type]));
    if (hasPendingAdd) return;

    // Check if already have highlighted items (already restored or just added)
    const hasHighlighted = interests.some((item) => item.highlighted);
    if (hasHighlighted) return;

    // Check restoration state for this type
    const state = restorationStateRef.current[interestType];
    if (state === 'pending' || state === 'done') return;

    // Get saved interests from context
    const savedInterests = context.current.audience_app[audienceChannel].audience_interestsToTest?.[interestType] || [];
    if (isEmpty(savedInterests)) return;

    // Mark as pending to prevent duplicate calls
    restorationStateRef.current[interestType] = 'pending';

    // Process the saved interests through the normal pipeline to fetch fresh data
    const typeAtStart = interestType; // Capture the type when this effect started
    processNewTestedInterests(interestType, savedInterests, getPromiseOfInterestsByTypeAndTopic, () => {
      // Check if user has switched tabs - don't update if they have
      if (currentInterestTypeRef.current !== typeAtStart) {
        return;
      }
      setTopic({ id: ALL_INTERESTS });
      doPublishAllInterestsToView(true, interestType, ALL_INTERESTS);
    })
      .then(() => {
        restorationStateRef.current[interestType] = 'done';
      })
      .catch(() => {
        // Reset state on error so it can retry
        restorationStateRef.current[interestType] = null;
      });
  }, [
    interestType,
    interests,
    audienceChannel,
    context,
    setTopic,
    processNewTestedInterests,
    getPromiseOfInterestsByTypeAndTopic,
    doPublishAllInterestsToView,
  ]);

  // Watch newlyAddedInterestsToTest
  useEffect(() => {
    if (interestType === 'categories') {
      const searchResults = newlyAddedInterestsToTest.categories;
      if (isEmpty(searchResults)) return;

      // Filter out invalid categories (same as phrases/websites do)
      // Don't clear invalid tags - let them stay visible with "(not tracked)" message
      const validResults = searchResults.filter((tag) => tag.id && !tag.invalid);
      if (isEmpty(validResults)) return;

      saveTestedInterestsToContext('categories', validResults);
      setTopic({ id: ALL_INTERESTS });
      const searchIds = validResults.map((r) => r.id);
      setInterests((prev) => {
        // First, add any categories that aren't already in the list
        const existingIds = new Set(prev?.map((item) => item.id) || []);
        const newCategories = validResults
          .filter((result) => !existingIds.has(result.id))
          .map((result) => ({
            ...result,
            highlighted: true,
            deletable: true,
            highlightMarkTooltip: 'Manually added',
            wasAddedFromSearch: true, // Mark as added from search for proper deletion
          }));

        // Then highlight existing items that match
        const mapped = prev?.map((item) =>
          searchIds.includes(item.id)
            ? { ...item, highlighted: true, deletable: true, highlightMarkTooltip: 'Manually added' }
            : item
        );

        // Combine: new categories + mapped existing
        const combined = [...newCategories, ...(mapped || [])];

        // Sort: highlighted at top (desc), then by uniqueness-index desc
        return orderBy(combined, ['highlighted', 'uniqueness-index'], ['desc', 'desc']);
      });
      setNewlyAddedInterestsToTest((prev) => ({ ...prev, categories: [] }));
      return;
    }
    interestTypes.forEach(({ value: type }) => {
      const searchResults = newlyAddedInterestsToTest[type];
      if (isEmpty(searchResults)) return;
      const validatedResults = searchResults.filter((tag) => tag.id && !tag.waiting && !tag.invalid);
      if (searchResults.some((tag) => tag.invalid || tag.waiting) || isEmpty(validatedResults)) return;
      saveTestedInterestsToContext(type, searchResults);
      // Clear the newly added interests immediately to prevent duplicate processing
      setNewlyAddedInterestsToTest((prev) => ({ ...prev, [type]: [] }));
      // Mark restoration as done to prevent it from also processing these interests
      restorationStateRef.current[type] = 'done';

      // Check if items exist in current interests - if so, highlight immediately (like categories)
      const searchIds = searchResults.map((r) => r.id);
      const itemsInCurrentInterests = interests?.filter((item) => searchIds.includes(item.id)) || [];
      const itemsNotInCurrentInterests = searchResults.filter((r) => !interests?.some((item) => item.id === r.id));

      // Immediately highlight items that are already in the chart (no API needed)
      if (itemsInCurrentInterests.length > 0) {
        setInterests((prev) => {
          const mapped = prev?.map((item) =>
            searchIds.includes(item.id)
              ? { ...item, highlighted: true, deletable: true, highlightMarkTooltip: 'Manually added' }
              : item
          );
          // Sort: highlighted at top (desc), then by uniqueness-index desc
          return orderBy(mapped, ['highlighted', 'uniqueness-index'], ['desc', 'desc']);
        });
      }

      // Only call processNewTestedInterests for items NOT in current interests (need API)
      if (itemsNotInCurrentInterests.length > 0) {
        const typeAtStart = type; // Capture the type when this callback started
        processNewTestedInterests(type, itemsNotInCurrentInterests, getPromiseOfInterestsByTypeAndTopic, () => {
          // Check if user has switched tabs - don't update if they have
          if (currentInterestTypeRef.current !== typeAtStart) {
            return;
          }
          setTopic({ id: ALL_INTERESTS });
          doPublishAllInterestsToView(true, type, ALL_INTERESTS);
        });
      }

      MixpanelAudience.trackTestingInterests(type, searchResults, false, audienceChannel);
    });
  }, [
    newlyAddedInterestsToTest,
    interestTypes,
    interestType,
    audienceChannel,
    setInterests,
    setTopic,
    saveTestedInterestsToContext,
    processNewTestedInterests,
    getPromiseOfInterestsByTypeAndTopic,
    doPublishAllInterestsToView,
    setNewlyAddedInterestsToTest,
  ]);
};

export default useInterestTesting;
